/**
 * BelegDetailPage — editierbarer Beleg mit Klassifikation + Bewirtung + PDF.
 *
 * Aufbau (vertikal):
 *   1. Header (Zurück + Lieferant-Titel)
 *   2. PDF-Vorschau-Tile (Tap → Vollbild)
 *   3. Pflicht-Felder Lieferant / Datum / Netto
 *   4. Zuordnung: beim Erfassen gewählte Kategorie als Anzeige (Material/
 *      Bewirtung/Helfer/Betrieb + Projekt/Helfer). „ändern"/unklassifizierte
 *      Mail-Importe zeigen das 4er-Auswahlraster in EINER Fläche. Bewirtung-
 *      Toggle als einzige Verfeinerung (nur Material/Bewirtung).
 *   5. Conditional:
 *      - Projekt-Dropdown wenn ausgabe_typ ∈ {lieferant, verpflegung}
 *      - Helfer-Dropdown wenn ausgabe_typ = helfer
 *      - BewirtungSection wenn ausgabe_typ = verpflegung
 *   6. Notiz, Status
 *   7. Speichern-Button (Sticky-Footer)
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Banknote,
  Building2,
  Briefcase,
  Check,
  CheckCircle2,
  Maximize2,
  Minus,
  Plus,
  Search,
  Sparkles,
  Trash2,
  UserCircle2,
  UtensilsCrossed,
  X,
} from "lucide-react";
import {
  api,
  type AusgabeTyp,
  type Beleg,
  type BelegPatch,
  type Duplicate,
  type Lieferant,
  istEntwurfStatus,
} from "../lib/api";
import { useVollbildZurueck } from "../lib/vollbild";
import BewirtungSection from "../components/beleg/BewirtungSection";
import LieferscheinPositionen from "../components/beleg/LieferscheinPositionen";
import ClaraHinweis from "../components/beleg/ClaraHinweis";
import ProjektWahl from "../components/beleg/ProjektWahl";

export default function BelegDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();

  const belegQ = useQuery({
    queryKey: ["beleg", id],
    queryFn: () => api.getBeleg(id),
    enabled: !!id,
    // Solange Clara liest, alle 2,5 s nachsehen. Eigene Eingaben gehen dabei
    // nicht verloren: sie liegen in `form` über den Daten.
    refetchInterval: (q) => (q.state.data?.ki_laeuft ? 2500 : false),
  });
  // Clara ist fertig → was an ihrem Ergebnis hängt, frisch holen.
  const claraLiest = !!belegQ.data?.ki_laeuft;
  const [claraVorher, setClaraVorher] = useState(claraLiest);
  useEffect(() => {
    if (claraVorher && !claraLiest) {
      qc.invalidateQueries({ queryKey: ["lieferschein-positionen", id] });
      qc.invalidateQueries({ queryKey: ["beleg-duplicates", id] });
      qc.invalidateQueries({ queryKey: ["belege-recent"] });
    }
    setClaraVorher(claraLiest);
  }, [claraLiest]); // eslint-disable-line react-hooks/exhaustive-deps
  // Auch abgeschlossene Projekte — der Server liefert sie seit 19.10.2026 mit.
  const projekteQ = useQuery({
    queryKey: ["projekte-zuordnung"],
    queryFn: () => api.listProjekte({}),
    staleTime: 5 * 60 * 1000,
  });
  const inhaberQ = useQuery({
    queryKey: ["scanflow-inhaber"],
    queryFn: () => api.listInhaber(),
    staleTime: 60 * 60 * 1000,
  });
  const teamQ = useQuery({
    queryKey: ["scanflow-team"],
    queryFn: () => api.listTeam(),
    staleTime: 60 * 60 * 1000,
  });
  const lieferantenQ = useQuery({
    queryKey: ["scanflow-lieferanten"],
    queryFn: () => api.listLieferanten(),
    staleTime: 60 * 60 * 1000,
  });
  const dupQ = useQuery({
    queryKey: ["beleg-duplicates", id],
    queryFn: () => api.findDuplicates(id),
    // Auch nach dem Abschluss (03.11.2026): eine Dublette fällt oft erst später auf.
    enabled: !!id && !!belegQ.data && belegQ.data.status !== "verworfen",
    staleTime: 30 * 1000,
  });
  const dubletten = (dupQ.data ?? []).filter((d: Duplicate) => d.status !== "verworfen");
  const [verwerfenGrund, setVerwerfenGrund] = useState("");
  const verwerfenM = useMutation({
    mutationFn: (grund: string) => api.verwerfenBeleg(id, grund),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["beleg", id] });
      qc.invalidateQueries({ queryKey: ["belege-recent"] });
      qc.invalidateQueries({ queryKey: ["datev-pending"] });
      if (r.modus === "storniert") window.alert(r.hinweis);
      nav("/inbox", { replace: true });
    },
  });

  const [form, setForm] = useState<BelegPatch>({});
  const [fullscreen, setFullscreen] = useState(false);
  useVollbildZurueck(fullscreen, () => { setFullscreen(false); setZoom(1); });
  // Vollbild-Zoomstufe (1–4×) zum Prüfen von IBAN/Betrag/Lieferant auf dem Beleg.
  const [zoom, setZoom] = useState(1);
  // Klassifikation wurde beim Erfassen gewählt → in der Detailansicht primär
  // anzeigen. Auswahl-Raster nur bei „ändern" (oder unklassifizierten Mail-Importen).
  const [editKlass, setEditKlass] = useState(false);
  useEffect(() => {
    if (belegQ.data) setForm({});
  }, [belegQ.data?.id]);

  const patchM = useMutation({
    mutationFn: (patch: BelegPatch) => api.patchBeleg(id, patch),
    onSuccess: (b) => {
      qc.setQueryData(["beleg", id], b);
      qc.invalidateQueries({ queryKey: ["belege-recent"] });
      // Zahllauf + DATEV-Karte hängen am Status → mit auffrischen.
      qc.invalidateQueries({ queryKey: ["zahllauf"] });
      qc.invalidateQueries({ queryKey: ["datev-pending"] });
      setForm({});
    },
  });

  const deleteM = useMutation({
    mutationFn: () => api.deleteBeleg(id),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ["beleg", id] });
      qc.invalidateQueries({ queryKey: ["belege-recent"] });
      // Zahllauf + DATEV-Karte hängen am Status → mit auffrischen.
      qc.invalidateQueries({ queryKey: ["zahllauf"] });
      qc.invalidateQueries({ queryKey: ["datev-pending"] });
      nav("/inbox", { replace: true });
    },
  });

  const finalizeM = useMutation({
    mutationFn: () => api.finalizeBeleg(id),
    onSuccess: (b) => {
      qc.setQueryData(["beleg", id], b);
      qc.invalidateQueries({ queryKey: ["belege-recent"] });
      // Zahllauf + DATEV-Karte hängen am Status → mit auffrischen.
      qc.invalidateQueries({ queryKey: ["zahllauf"] });
      qc.invalidateQueries({ queryKey: ["datev-pending"] });
    },
  });

  // Clara (noch einmal) lesen lassen — v.a. für Mail-Importe ohne erkannte
  // Felder. Läuft im Hintergrund; die Seite fragt nach, bis sie fertig ist.
  // Eigene Eingaben bleiben stehen (Clara füllt nur leere Felder).
  const extractM = useMutation({
    mutationFn: () => api.lesenLassen(id),
    onSuccess: (b) => {
      qc.setQueryData(["beleg", id], b);
      qc.invalidateQueries({ queryKey: ["belege-recent"] });
    },
  });

  const [confirmDelete, setConfirmDelete] = useState(false);

  function setField<K extends keyof BelegPatch>(k: K, v: BelegPatch[K]) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  /** Aktueller Anzeige-Wert: erst form (Edit-Buffer), dann DB-Wert */
  function val<K extends keyof Beleg>(k: K): Beleg[K] | undefined {
    if (k in form) return (form as any)[k];
    return belegQ.data?.[k];
  }

  const hasChanges = Object.keys(form).length > 0;
  const b = belegQ.data;
  const ausgabeTyp: AusgabeTyp | "" = (val("ausgabe_typ") as AusgabeTyp) ?? "";

  // Welche Scan-Warnungen sind nach den bisherigen Korrekturen noch offen?
  // Der Lieferant gilt als geklärt, sobald ein kanonischer Lieferant gewählt
  // oder neu angelegt wurde (lieferant_firma_id gesetzt) — dann nicht mehr
  // warnen (sonst nervt die alte Scan-Warnung trotz Korrektur).
  const firmaGesetzt = String(val("lieferant_firma_id") ?? "").trim() !== "";
  // Qualitäts-Banner zeigt NUR Bild-/Extraktionswarnungen. Lieferant-Zeilen
  // (Altdaten vor 11.06.2026) werden immer rausgefiltert — Lieferant-Zuordnung
  // hat einen eigenen Hinweis unten.
  const offeneWarnung = (b?.scan_warnung || "")
    .split("; ")
    .filter((s) => s.trim() && !/lieferant/i.test(s))
    .join("; ");

  // Bewirtung: bei Datums-Match die dem Tagesprojekt zugewiesenen Personen vorwählen.
  const bewProjektId = String(val("projekt_id") ?? "");
  const bewDatum = String(val("datum") ?? "");
  const bewEligible =
    ausgabeTyp === "verpflegung" &&
    !!bewProjektId &&
    /^\d{4}-\d{2}-\d{2}$/.test(bewDatum);
  const personenQ = useQuery({
    queryKey: ["projekt-personen", bewProjektId, bewDatum],
    queryFn: () => api.projektPersonen(bewProjektId, bewDatum),
    enabled: bewEligible,
    staleTime: 5 * 60 * 1000,
  });
  const [prefilledKey, setPrefilledKey] = useState("");
  useEffect(() => {
    if (!bewEligible) return;
    const ids = personenQ.data;
    if (!ids || ids.length === 0) return;
    const key = `${bewProjektId}|${bewDatum}`;
    if (prefilledKey === key) return;
    setPrefilledKey(key);
    const currentIntern =
      ("bewirtung_teilnehmer_intern" in form
        ? (form.bewirtung_teilnehmer_intern as string[])
        : belegQ.data?.bewirtung_teilnehmer_intern) ?? [];
    if (currentIntern.length > 0) return; // bestehende Auswahl nicht überschreiben
    setForm((prev) => ({ ...prev, bewirtung_teilnehmer_intern: ids }));
  }, [personenQ.data, bewEligible, bewProjektId, bewDatum, prefilledKey, form, belegQ.data]);

  if (belegQ.isLoading) {
    return (
      <div className="m-app">
        <PageHeader onBack={() => nav(-1)} title="Beleg" />
        <div style={{ padding: 24, color: "var(--m-ink-3)" }}>Lade…</div>
      </div>
    );
  }
  if (belegQ.isError || !b) {
    return (
      <div className="m-app">
        <PageHeader onBack={() => nav(-1)} title="Beleg" />
        <div style={{ padding: 24, color: "tomato" }}>
          {(belegQ.error as Error)?.message ?? "Rechnung nicht gefunden."}
        </div>
      </div>
    );
  }

  // Lieferscheine haben keinen Betrag, sondern Positionen → eigener Review-Screen.
  if (b.beleg_typ === "lieferschein") {
    return <LieferscheinPositionen beleg={b} onLesen={() => extractM.mutate()} />;
  }

  // Bewirtung-Form sub-state (binding gegen die conditional Section)
  const bewirtungForm = {
    bewirtung_anlass: (val("bewirtung_anlass") as string) ?? "",
    bewirtung_teilnehmer_intern:
      (val("bewirtung_teilnehmer_intern") as string[]) ?? [],
    bewirtung_teilnehmer_extern:
      (val("bewirtung_teilnehmer_extern") as string[]) ?? [],
    bewirtung_trinkgeld: (val("bewirtung_trinkgeld") as number) ?? 0,
  };

  function setBewirtung(next: typeof bewirtungForm) {
    setForm((prev) => ({
      ...prev,
      bewirtung_anlass: next.bewirtung_anlass,
      bewirtung_teilnehmer_intern: next.bewirtung_teilnehmer_intern,
      bewirtung_teilnehmer_extern: next.bewirtung_teilnehmer_extern,
      bewirtung_trinkgeld: next.bewirtung_trinkgeld,
    }));
  }

  // #22: Klassifikation bleibt änderbar (z.B. Material → Verpflegung, wenn falsch
  // einsortiert) — gesperrt wird erst, wenn der Beleg an DATEV versendet wurde.
  // Das Umklassifizieren bucht über beleg_patch → _upsert_projekt_ausgabe sauber um.
  const klassifikationGelockt = !!b.an_steuerberater_versendet_at;
  // Bewirtung = ausgabe_typ 'verpflegung'. Als eigener Toggle, weil beim Erfassen oft
  // vergessen — der Nutzer setzt es erst hier beim Verarbeiten.
  function pickTyp(t: AusgabeTyp) {
    if (klassifikationGelockt) return;
    const isBewirtung = t === "verpflegung";
    setForm((prev) => ({
      ...prev,
      ausgabe_typ: t,
      is_bewirtung: isBewirtung,
      // Projekt-Reset wenn betrieb / helfer
      ...(t === "betrieb" ? { projekt_id: "" } : {}),
    }));
  }

  // Klassifikations-Metadaten — eine Quelle für Anzeige UND Auswahl-Raster.
  const KLASS: Record<
    AusgabeTyp,
    { icon: React.ReactNode; label: string; hint: string; color: string }
  > = {
    lieferant: { icon: <Building2 size={20} />, label: "Projektkosten", hint: "Material, Pflanzen, Fremdleistung fürs Projekt", color: "#3b82f6" },
    verpflegung: { icon: <UtensilsCrossed size={20} />, label: "Bewirtung", hint: "§4 EStG — Anlass & Teilnehmer", color: "#ea580c" },
    helfer: { icon: <UserCircle2 size={20} />, label: "Helfer-Rechnung", hint: "Rechnung von einem Helfer", color: "#8b5cf6" },
    betrieb: { icon: <Briefcase size={20} />, label: "Betriebskosten", hint: "Sprit, Werkzeug, Büro — ohne Projekt", color: "#f59e0b" },
  };
  // Auswahl-Raster nur wenn unklassifiziert (Mail-Import) oder explizit „ändern".
  const showKlassSelector = !klassifikationGelockt && (editKlass || !ausgabeTyp);
  // Detailzeile der Anzeige (Projekt- bzw. Helfername).
  const zuordnungDetail = (() => {
    if (ausgabeTyp === "betrieb") return "ohne Projekt";
    if (ausgabeTyp === "helfer") {
      const h = teamQ.data?.find((x) => x.id === String(val("helfer_id") ?? ""));
      return h ? h.name : "Helfer noch offen";
    }
    if (ausgabeTyp === "lieferant" || ausgabeTyp === "verpflegung") {
      const p = projekteQ.data?.find((x) => x.id === String(val("projekt_id") ?? ""));
      return p ? p.title : "kein Projekt";
    }
    return "";
  })();

  // Beträge: Brutto (Kontozuordnung) + Netto (Projektausgabe). MwSt = Differenz.
  const bruttoVal = Number(val("bruttobetrag") ?? 0);
  const nettoVal = Number(val("netto") ?? 0);
  const mwstVal =
    bruttoVal > 0 && nettoVal > 0
      ? Math.round((bruttoVal - nettoVal) * 100) / 100
      : 0;

  // #18: Wenn nur Brutto ODER nur Netto erkannt wurde, MwSt-Satz wählen und den
  // fehlenden Wert berechnen (statt pauschal gleichzusetzen). 0% = Helfer/keine MwSt.
  const nurEinBetrag = bruttoVal > 0 !== nettoVal > 0;
  function applyMwstSatz(satz: number) {
    if (bruttoVal > 0 && nettoVal <= 0) {
      const netto = Math.round((bruttoVal / (1 + satz)) * 100) / 100;
      setField("netto", netto);
      setField("mwst_betrag", Math.round((bruttoVal - netto) * 100) / 100);
    } else if (nettoVal > 0 && bruttoVal <= 0) {
      const brutto = Math.round(nettoVal * (1 + satz) * 100) / 100;
      setField("bruttobetrag", brutto);
      setField("mwst_betrag", Math.round((brutto - nettoVal) * 100) / 100);
    }
  }

  /** Patch inkl. abgeleiteter MwSt (nur wenn Brutto/Netto im Edit-Buffer). */
  function finalPatch(): BelegPatch {
    const p: BelegPatch = { ...form };
    if (
      ("bruttobetrag" in form || "netto" in form) &&
      bruttoVal > 0 &&
      nettoVal > 0
    ) {
      p.mwst_betrag = Math.round((bruttoVal - nettoVal) * 100) / 100;
    }
    return p;
  }

  // Angehängte Seiten am PDF (von hinten): Bewirtungsanhang, dann Eigenbeleg.
  const anhaenge: { page: number; label: string }[] = [
    ...(b.is_bewirtung && b.bewirtung_annex_at ? [{ page: b.eigenbeleg_at ? -2 : -1, label: "§4-Bewirtungsbeleg" }] : []),
    ...(b.eigenbeleg_at ? [{ page: -1, label: "Eigenbeleg" }] : []),
  ];
  const versendet = !!b.an_steuerberater_versendet_at;
  // Entwurf = noch nicht verarbeitet/gebucht. Abschließen ruft /finalize.
  const isDraft = istEntwurfStatus(b.status);
  // Zahlungs-/Buchungsstatus für die Footer-Logik (F8):
  //   istBezahlt = bereits als bezahlt markiert → nur noch bestätigen, kein 2. „Bezahlt".
  //   hatBuchung = einer Bankzeile zugeordnet (oder bar / privat ausgelegt) → „Offen" ergibt keinen Sinn mehr.
  const istBezahlt = !!val("is_bezahlt");
  const hatBuchung = !!b.kontobewegung_id || val("zahlungsart") === "bar" || val("zahlungsweg") === "inhaber";

  /** Beleg abschließen: offene Änderungen speichern, bei Entwurf zusätzlich
   *  finalisieren (buchen), dann zurück zur Inbox. */
  // „Überweisen" gleich beim ersten Abschließen (16.10.2026): der Beleg geht
  // offen in den Ausgangskorb und von dort mit der nächsten Bank-Freigabe raus.
  const kannUeberweisen =
    !istBezahlt &&
    !b.kontobewegung_id &&
    val("zahlungsart") !== "bar" &&
    !val("is_privatentnahme") &&
    (val("zahlungsweg") || "gbr") === "gbr" &&
    !["angewiesen", "ausgefuehrt"].includes(b.ueberweisung_status || "none");

  /* Bewirtung (§ 4 Abs. 5 Nr. 2 EStG): ohne Anlass, Teilnehmer und Projekt entsteht
     kein Anhang, und ohne Anhang geht der Beleg nicht an DATEV (03.11.2026). */
  const bewirtungFehlt: string[] = ausgabeTyp !== "verpflegung" ? [] : [
    ...(bewirtungForm.bewirtung_anlass.trim() ? [] : ["Anlass"]),
    ...(bewirtungForm.bewirtung_teilnehmer_intern.length + bewirtungForm.bewirtung_teilnehmer_extern.length > 0 ? [] : ["Teilnehmer"]),
    ...(String(val("projekt_id") ?? "").trim() ? [] : ["Projekt"]),
  ];

  async function handleConfirm(bezahlt?: boolean, ueberweisen?: boolean) {
    if (bewirtungFehlt.length) {
      window.alert(
        `Für den Bewirtungsbeleg fehlt noch: ${bewirtungFehlt.join(", ")}.\n\n`
          + "Erst damit entsteht der Bewirtungsanhang (§ 4 EStG), und erst dann geht der Beleg an DATEV.",
      );
      return;
    }
    // Kein Beleg ohne vollständigen Brutto/Netto. Fehlt einer, muss der MwSt-Satz
    // gewählt werden (Auswahl oben) — nicht mehr pauschal gleichsetzen.
    if (isDraft && (bruttoVal <= 0 || nettoVal <= 0)) {
      window.alert(
        bruttoVal > 0 || nettoVal > 0
          ? "Bitte oben den MwSt-Satz wählen (oder den fehlenden Betrag eingeben) — der fehlende Wert wird dann berechnet."
          : "Bitte einen Betrag eingeben. Eine Rechnung darf nicht ohne Brutto/Netto abgeschlossen werden.",
      );
      return;
    }
    // Gleicher Beleg schon da (03.11.2026: Zscheile ging zweimal an DATEV).
    if (isDraft && dubletten.length) {
      const d = dubletten[0];
      const ok = window.confirm(
        `Diesen Beleg gibt es wahrscheinlich schon: ${d.lieferant || "—"}, ${fmtDatum(d.datum)}, ${d.bruttobetrag.toFixed(2).replace(".", ",")} €.\n\nTrotzdem abschließen? Er geht dann ein zweites Mal an DATEV.`,
      );
      if (!ok) return;
    }
    // Verbleibende Scan-Warnungen (Betrag ist jetzt erzwungen) weich bestätigen.
    if (isDraft) {
      const restW = offeneWarnung
        .split("; ")
        .filter((s) => s.trim() && !/betrag/i.test(s))
        .join("; ");
      if (restW) {
        const ok = window.confirm(`Aus der Scan-Prüfung: ${restW}.\n\nTrotzdem abschließen?`);
        if (!ok) return;
      }
      // Empfänger-Zuordnung: bei Helferrechnungen zählt der Helfer, sonst der Lieferant.
      // (Eine Helferrechnung hat keinen Lieferanten — danach zu fragen war unlogisch.)
      if (ausgabeTyp === "helfer") {
        if (!String(val("helfer_id") ?? "").trim()) {
          const ok = window.confirm("Es ist noch kein Helfer ausgewählt.\n\nTrotzdem abschließen?");
          if (!ok) return;
        }
      } else if (!firmaGesetzt) {
        const ok = window.confirm("Lieferant ist noch nicht zugeordnet.\n\nTrotzdem abschließen?");
        if (!ok) return;
      }
    }
    const p = finalPatch();
    if (bruttoVal > 0 && nettoVal > 0) p.mwst_betrag = Math.round((bruttoVal - nettoVal) * 100) / 100;
    if (bezahlt !== undefined) p.is_bezahlt = bezahlt;
    if (ueberweisen) p.im_zahllauf = true;
    try {
      if (Object.keys(p).length > 0) await patchM.mutateAsync(p);
      if (isDraft) await finalizeM.mutateAsync();
      nav("/inbox");
    } catch {
      /* Fehler über patchM/finalizeM.isError sichtbar */
    }
  }

  return (
    <div className="m-app" style={{ minHeight: "100vh" }}>
      <PageHeader
        onBack={() => nav(-1)}
        title={b.lieferant || "Beleg"}
        subtitle={fmtDatum(b.datum) || "—"}
      />

      <div style={{ padding: "0 16px 130px" }}>
        {dubletten.length > 0 && (
          <div
            style={{
              margin: "10px 0 0",
              padding: "12px 14px",
              borderRadius: 12,
              background: "rgba(224,130,114,0.10)",
              border: "1px solid #e08272",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#c0392b", fontWeight: 700, fontSize: 14 }}>
              <AlertTriangle size={16} /> Möglicher doppelter Beleg
            </div>
            <div style={{ fontSize: 12, color: "var(--m-ink-2)", margin: "6px 0 10px" }}>
              Es gibt bereits {dubletten.length === 1 ? "einen ähnlichen Beleg" : `${dubletten.length} ähnliche Belege`}. Ist es derselbe, ganz unten „{b.an_steuerberater_versendet_at ? "Als Fehlerfassung verwerfen" : "Rechnung löschen"}" wählen.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {dubletten.map((d: Duplicate) => (
                <button
                  key={d.id}
                  onClick={() => nav(`/beleg/${d.id}`)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px solid var(--m-line)",
                    background: "var(--m-card)",
                    color: "var(--m-ink)",
                    fontSize: 12,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {d.lieferant || "—"} · {fmtDatum(d.datum)} · {d.bruttobetrag.toFixed(2)}€
                  </span>
                  <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, color: "var(--m-ink-3)" }}>
                    {d.grund === "hash" ? "identisch" : d.grund === "betrag_datum" ? "Betrag & Datum gleich" : "gleiche Werte"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        {isDraft && (
          <ClaraHinweis
            laeuft={claraLiest}
            fehler={!b.ki_extrahiert ? b.ki_fehler : ""}
            onNochmal={() => extractM.mutate()}
          />
        )}
        {/* A2: Scan-Qualitätswarnung (KI-Konfidenz + Heuristik) */}
        {isDraft && offeneWarnung.trim() && (
          <div
            style={{
              margin: "10px 0 0",
              padding: "12px 14px",
              borderRadius: 12,
              background: "rgba(227,176,97,0.12)",
              border: "1px solid #e3b061",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#c8862f", fontWeight: 700, fontSize: 14 }}>
              <AlertTriangle size={16} /> Scan-Qualität prüfen
            </div>
            <div style={{ fontSize: 12, color: "var(--m-ink-2)", margin: "6px 0 0" }}>
              {offeneWarnung}. Bitte die markierten Werte gegen den Beleg prüfen — bei
              schlechtem Foto lieber neu aufnehmen.
            </div>
          </div>
        )}
        {/* Lieferant-Zuordnung: eigener, neutraler Hinweis (kein Qualitätsproblem).
            Helferrechnungen haben keinen Lieferanten — der Helfer ist über
            ausgabe_typ='helfer' + helfer_id bereits bekannt (Team-Upload). */}
        {isDraft && !claraLiest && ausgabeTyp !== "helfer" && !firmaGesetzt && (
          <div
            style={{
              margin: "10px 0 0",
              padding: "12px 14px",
              borderRadius: 12,
              background: "rgba(96,148,227,0.10)",
              border: "1px solid #6094e3",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#3c6fc4", fontWeight: 700, fontSize: 14 }}>
              <Building2 size={16} /> Lieferant zuordnen
            </div>
            <div style={{ fontSize: 12, color: "var(--m-ink-2)", margin: "6px 0 0" }}>
              Der erkannte Lieferant ist noch nicht in der Lieferantenliste. Unten auswählen
              oder neu anlegen — mit dem Scan ist alles in Ordnung.
            </div>
          </div>
        )}
        {/* PDF-Vorschau. Bei Bewirtung mit erzeugtem §4-Annex: Original-Scan zuerst,
            seitlich wischbar zum angereicherten §4-Abschnitt (beide sichtbar). */}
        {anhaenge.length ? (
          <div style={{ margin: "10px 0 18px" }}>
            <div
              style={{
                display: "flex",
                gap: 10,
                overflowX: "auto",
                scrollSnapType: "x mandatory",
                WebkitOverflowScrolling: "touch",
                paddingBottom: 6,
              }}
            >
              {[{ page: 0, label: "Original-Scan" }, ...anhaenge].map((s) => (
                <button
                  key={s.page}
                  onClick={() => setFullscreen(true)}
                  style={{
                    position: "relative",
                    flex: "0 0 86%",
                    scrollSnapAlign: "center",
                    padding: 0,
                    border: "1px solid var(--m-line)",
                    borderRadius: 14,
                    background: "var(--m-card)",
                    overflow: "hidden",
                    cursor: "zoom-in",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      top: 8,
                      left: 8,
                      zIndex: 1,
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "3px 8px",
                      borderRadius: 999,
                      background: s.page === -1 ? "rgba(234,88,12,0.92)" : "rgba(0,0,0,0.6)",
                      color: "white",
                    }}
                  >
                    {s.label}
                  </span>
                  <img
                    src={api.thumbnailUrl(b.id, 700, s.page)}
                    alt={s.label}
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "60vh",
                      objectFit: "contain",
                      objectPosition: "top",
                      background: "white",
                      display: "block",
                    }}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                </button>
              ))}
            </div>
            <div style={{ textAlign: "center", fontSize: 11, color: "var(--m-ink-3)", marginTop: 2 }}>
              ← wischen: Original &amp; §4-Beleg · tippen für Vollbild
            </div>
          </div>
        ) : (
          <button
            onClick={() => setFullscreen(true)}
            style={{
              position: "relative",
              display: "block",
              width: "100%",
              margin: "10px 0 18px",
              padding: 0,
              border: "1px solid var(--m-line)",
              borderRadius: 14,
              background: "var(--m-card)",
              overflow: "hidden",
              cursor: "zoom-in",
            }}
          >
            <img
              src={api.thumbnailUrl(b.id, 700)}
              alt="Beleg-Vorschau"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "60vh",
                objectFit: "contain",
                objectPosition: "top",
                background: "white",
                display: "block",
              }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
            <div
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                background: "rgba(0,0,0,0.6)",
                color: "white",
                padding: "4px 10px",
                borderRadius: 8,
                fontSize: 12,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Maximize2 size={12} /> Vollbild
            </div>
          </button>
        )}

        {/* KI erneut analysieren — nur bei Entwürfen; füllt Lieferant/Datum/Beträge
            nach (alle PDF-Seiten). */}
        {isDraft && (
        <>
        <button
          onClick={() => extractM.mutate()}
          disabled={extractM.isPending || claraLiest}
          style={{
            width: "100%",
            marginTop: 14,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "12px 14px",
            borderRadius: 12,
            border: "1px solid var(--m-accent, #6db04a)",
            background: "var(--m-accent-soft, rgba(193,74,61,0.12))",
            color: "var(--m-accent, #6db04a)",
            fontSize: 14,
            fontWeight: 700,
            cursor: extractM.isPending || claraLiest ? "not-allowed" : "pointer",
            opacity: extractM.isPending || claraLiest ? 0.6 : 1,
          }}
        >
          <Sparkles size={16} />
          {claraLiest || extractM.isPending ? "Clara liest …" : "Von Clara lesen lassen"}
        </button>
        {extractM.isError && (
          <div style={{ fontSize: 12, color: "tomato", marginTop: 6, textAlign: "center" }}>
            {(extractM.error as Error).message}
          </div>
        )}
        </>
        )}

        {/* Grunddaten */}
        <Section>
          {b.ki_extrahiert && (
            <KiHerkunft
              konfidenz={b.scan_konfidenz}
              lieferant={String(val("lieferant") ?? "")}
              datum={String(val("datum") ?? "")}
              brutto={bruttoVal}
            />
          )}
          <LieferantField
            roh={String(val("lieferant") ?? "")}
            firmaId={String(val("lieferant_firma_id") ?? "")}
            lieferanten={lieferantenQ.data ?? []}
            isHelfer={ausgabeTyp === "helfer"}
            onRohChange={(v) => setField("lieferant", v)}
            onPickFirma={(fid) => setField("lieferant_firma_id", fid)}
          />
          <Field label="Datum">
            <input
              type="date"
              value={String(val("datum") ?? "")}
              onChange={(e) => setField("datum", e.target.value)}
              style={{ ...inputStyle, minWidth: 0 }}
            />
          </Field>
          {(b.ki_beschreibung || "").trim() && (
            <Field label="Inhalt (von Clara gelesen)">
              <div style={{ fontSize: 13, color: "var(--m-ink-2)", padding: "2px 0" }}>
                {b.ki_beschreibung}
              </div>
            </Field>
          )}
          <Row>
            <Field label="Brutto (€)">
              <MoneyInput
                value={bruttoVal}
                onCommit={(n) => setField("bruttobetrag", n)}
                placeholder="Gesamtbetrag"
              />
            </Field>
            <Field label="Netto (€)">
              <MoneyInput
                value={nettoVal}
                onCommit={(n) => setField("netto", n)}
                placeholder="leer = Brutto"
              />
            </Field>
          </Row>
          {nurEinBetrag && (
            <div style={{ margin: "10px 2px 0" }}>
              <div style={{ fontSize: 12, color: "#c8862f", fontWeight: 600, marginBottom: 6 }}>
                {bruttoVal > 0 ? "Netto fehlt" : "Brutto fehlt"} — MwSt-Satz wählen, dann wird der Wert berechnet:
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  { label: "19 %", satz: 0.19 },
                  { label: "7 %", satz: 0.07 },
                  { label: "0 % (Helfer)", satz: 0 },
                ].map((o) => (
                  <button
                    key={o.label}
                    onClick={() => applyMwstSatz(o.satz)}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      border: "1px solid #e3b061",
                      background: "transparent",
                      color: "var(--m-ink)",
                    }}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{ fontSize: 11, color: "var(--m-ink-3)", margin: "8px 2px 0", lineHeight: 1.6 }}>
            MwSt (errechnet): <strong>{mwstVal > 0 ? `${mwstVal.toFixed(2).replace(".", ",")} €` : "—"}</strong>
            <br />
            Brutto = Kontozuordnung · Netto = Projektausgabe
          </div>
        </Section>

        {/* ── Zuordnung ─────────────────────────────────────────────────────
            Die Kategorie wurde beim Erfassen gewählt → hier primär ANZEIGEN,
            nicht erneut abfragen. Das volle Auswahl-Raster (Material · Bewirtung ·
            Helfer · Betrieb in EINER Fläche) erscheint nur bei unklassifizierten
            Belegen (Mail-Import) oder per „ändern". Im Anzeige-Modus ist der
            Bewirtungs-Toggle die einzige sinnvolle Verfeinerung — und nur für
            Material/Bewirtung relevant. */}
        <div style={{ marginTop: 22 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              margin: "0 0 8px 4px",
            }}
          >
            <h2
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--m-ink-3)",
                textTransform: "uppercase",
                letterSpacing: 0.6,
                margin: 0,
              }}
            >
              Zuordnung
            </h2>
            {!klassifikationGelockt && ausgabeTyp ? (
              <button
                onClick={() => setEditKlass((o) => !o)}
                style={linkBtn}
              >
                {showKlassSelector ? "fertig" : "ändern"}
              </button>
            ) : null}
          </div>

          {showKlassSelector ? (
            /* Auswahl-Raster: alle vier Klassifikationen in EINER Fläche. */
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
              {(["lieferant", "verpflegung", "helfer", "betrieb"] as const).map((typ) => (
                <TypTile
                  key={typ}
                  icon={KLASS[typ].icon}
                  label={KLASS[typ].label}
                  hint={KLASS[typ].hint}
                  color={KLASS[typ].color}
                  active={ausgabeTyp === typ}
                  onClick={() => {
                    pickTyp(typ);
                    setEditKlass(false);
                  }}
                />
              ))}
            </div>
          ) : ausgabeTyp ? (
            <>
              {/* Anzeige der beim Erfassen gewählten Zuordnung. */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: "1px solid var(--m-line)",
                  background: "var(--m-card)",
                }}
              >
                <span style={{ color: KLASS[ausgabeTyp].color, display: "flex" }}>
                  {KLASS[ausgabeTyp].icon}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--m-ink)" }}>
                    {KLASS[ausgabeTyp].label}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--m-ink-3)", marginTop: 1 }}>
                    {zuordnungDetail}
                  </div>
                </div>
              </div>
            </>
          ) : null}

          {/* Bewirtungs-Details direkt unter der Klassifikation — nur wenn als
              Bewirtungsbeleg erfasst. Nach der Erfassung nicht mehr umschaltbar,
              daher kein Toggle und kein separater Doppel-Block mehr. */}
          {ausgabeTyp === "verpflegung" && (
            <div style={{ marginTop: 10 }}>
              <BewirtungSection value={bewirtungForm} onChange={setBewirtung} />
              {/* Prüfen können, ob der Anhang da ist (03.11.2026). */}
              <div
                role="status"
                style={{
                  marginTop: 8, padding: "9px 12px", borderRadius: 10, fontSize: 12.5, lineHeight: 1.4,
                  background: bewirtungFehlt.length ? "rgba(234, 88, 12, 0.10)" : "rgba(109, 176, 74, 0.12)",
                  color: "var(--m-ink-2, var(--m-ink))",
                }}
              >
                {bewirtungFehlt.length
                  ? <>Fehlt noch: <b>{bewirtungFehlt.join(", ")}</b>. Erst dann entsteht der Bewirtungsanhang und der Beleg geht an DATEV.</>
                  : b.bewirtung_annex_at && !hasChanges
                    ? <>✓ Bewirtungsanhang erstellt am {fmtDatum(b.bewirtung_annex_at.slice(0, 10))} — im PDF angehängt
                        {b.an_steuerberater_versendet_at ? <>, an DATEV am {fmtDatum(b.an_steuerberater_versendet_at.slice(0, 10))}</> : ""}.</>
                    : <>Alles da — der Bewirtungsanhang entsteht beim Speichern bzw. Abschließen.</>}
              </div>
            </div>
          )}
        </div>

        {/* Bezahlung / Inhaber-Sonderfall (Migration 046). Drei sich ausschließende
            Fälle: GbR (Standard) · privat ausgelegt → Einlage · Privatkauf → Entnahme.
            Wirkt auf die Auszahlungssumme des angemeldeten Inhabers; für Nicht-Inhaber
            ohne Effekt (Server bucht nur, wenn der Nutzer ein owner ist). */}
        {/* Der Zahlweg ist eine interne Gesellschafter-Sache — wer wem was schuldet.
            Am Belegbild, das der Steuerberater bekommen hat, ändert er nichts, deshalb
            bleibt er auch nach dem Versand änderbar. Die Klassifikation darüber
            (Material/Verpflegung/Helfer) bleibt gesperrt wie bisher. */}
        <>
            <h2
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--m-ink-3)",
                textTransform: "uppercase",
                letterSpacing: 0.6,
                margin: "22px 0 8px 4px",
              }}
            >
              Bezahlung
            </h2>
            {klassifikationGelockt && (
              <div style={{ fontSize: 11, color: "var(--m-ink-3)", margin: "-2px 0 8px 4px" }}>
                Beleg ist schon beim Steuerberater — der Zahlweg lässt sich weiter ändern,
                er wirkt nur auf das Gesellschafterkonto.
              </div>
            )}
            {(() => {
              const sel = val("is_privatentnahme")
                ? "entnahme"
                : val("zahlungsweg") === "inhaber"
                  ? "einlage"
                  : "gbr";
              const opts = [
                { key: "gbr", label: "GbR", hint: "Firma zahlt" },
                { key: "einlage", label: "Privat ausgelegt", hint: "Ich zahle → Einlage" },
                { key: "entnahme", label: "Privatkauf", hint: "Privat → Entnahme" },
              ] as const;
              // Ohne Inhaber bucht der Server nichts. Deshalb beim Umschalten den
              // angemeldeten Inhaber vorbelegen, statt ihn hinterher aus dem Erfasser
              // zu raten — bei einem Helfer-Upload ginge das still daneben.
              const inhaberVorbelegt =
                String(val("inhaber_id") ?? "") || (inhaberQ.data?.find((i) => i.ich)?.id ?? "");
              function pick(k: string) {
                if (k === "einlage") {
                  setForm((p) => ({
                    ...p,
                    zahlungsweg: "inhaber",
                    is_privatentnahme: false,
                    inhaber_id: inhaberVorbelegt,
                  }));
                } else if (k === "entnahme") {
                  setForm((p) => ({
                    ...p,
                    zahlungsweg: "gbr",
                    is_privatentnahme: true,
                    inhaber_id: inhaberVorbelegt,
                  }));
                } else {
                  if (val("zahlungsart") === "bar") {
                    window.alert("Die GbR hat keine Barkasse — bar bezahlt hat immer ein Inhaber. Für eine Zahlung vom Firmenkonto unten „Konto“ wählen.");
                    return;
                  }
                  setForm((p) => ({ ...p, zahlungsweg: "gbr", is_privatentnahme: false }));
                }
              }
              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                  {opts.map((o) => {
                    const active = sel === o.key;
                    return (
                      <button
                        key={o.key}
                        onClick={() => pick(o.key)}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 2,
                          padding: "10px 12px",
                          borderRadius: 12,
                          cursor: "pointer",
                          textAlign: "left",
                          border: active ? "1.5px solid #3b82f6" : "1px solid var(--m-line)",
                          background: active ? "rgba(59,130,246,0.08)" : "var(--m-card)",
                          color: "var(--m-ink)",
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 700 }}>{o.label}</span>
                        <span style={{ fontSize: 10, color: "var(--m-ink-3)" }}>{o.hint}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            {(val("is_privatentnahme") || val("zahlungsweg") === "inhaber") && (
              <div style={{ fontSize: 11, color: "var(--m-ink-3)", margin: "6px 0 0 4px" }}>
                {val("is_privatentnahme")
                  ? "Privatkauf über die Firma — wird als Privatentnahme von deiner Auszahlung abgezogen."
                  : "Privat ausgelegt — wird als Einlage deiner Auszahlung gutgeschrieben."}
              </div>
            )}
            {ausgabeTyp !== "helfer" && (val("zahlungsart") === "bar" || val("zahlungsweg") === "inhaber") && (
              <div role="status" style={{
                margin: "8px 0 0", padding: "9px 12px", borderRadius: 10, fontSize: 12.5, lineHeight: 1.4,
                background: b.eigenbeleg_at && !hasChanges ? "rgba(109, 176, 74, 0.12)" : "rgba(59, 130, 246, 0.08)",
                color: "var(--m-ink-2, var(--m-ink))",
              }}>
                {b.eigenbeleg_at && !hasChanges
                  ? <>✓ Eigenbeleg erstellt am {fmtDatum(b.eigenbeleg_at.slice(0, 10))} — im PDF angehängt
                      {b.an_steuerberater_versendet_at ? <>, an DATEV am {fmtDatum(b.an_steuerberater_versendet_at.slice(0, 10))}</> : ""}.</>
                  : !String(val("inhaber_id") ?? "")
                    ? <>Wer hat bezahlt? Ohne Inhaber entsteht kein Eigenbeleg, und der Beleg geht nicht an DATEV.</>
                    : <>Beim Speichern bzw. Abschließen entsteht ein Eigenbeleg (Zahlung aus privaten Mitteln) als Seite im PDF für den Steuerberater.</>}
              </div>
            )}

            {(val("is_privatentnahme") || val("zahlungsweg") === "inhaber") && (
              <div style={{ marginTop: 10 }}>
                <label
                  style={{
                    fontSize: 11,
                    color: "var(--m-ink-3)",
                    display: "block",
                    margin: "0 0 4px 4px",
                  }}
                >
                  {val("is_privatentnahme") ? "Wessen Entnahme?" : "Wer hat bezahlt?"}
                </label>
                <select
                  value={String(val("inhaber_id") ?? "")}
                  onChange={(e) => setField("inhaber_id", e.target.value)}
                  style={inputStyle}
                >
                  <option value="">— Inhaber wählen —</option>
                  {inhaberQ.data?.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                      {i.ich ? " (du)" : ""}
                    </option>
                  ))}
                </select>
                {!String(val("inhaber_id") ?? "") && (
                  <div style={{ fontSize: 11, color: "#e0a53f", margin: "4px 0 0 4px" }}>
                    Ohne Inhaber wird nichts auf dem Gesellschafterkonto gebucht.
                  </div>
                )}
              </div>
            )}

            {/* Zahlungsart (Migration 056) — WIE gezahlt wurde, unabhängig vom Zahlungsweg.
                Bar = aus der Barkasse: keine Bankzeile, gilt als bezahlt, geht dennoch an DATEV. */}
            <h2
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--m-ink-3)",
                textTransform: "uppercase",
                letterSpacing: 0.6,
                margin: "22px 0 8px 4px",
              }}
            >
              Zahlungsart
            </h2>
            {(() => {
              const sel = val("zahlungsart") === "bar" ? "bar" : "konto";
              const opts = [
                { key: "konto", label: "Konto", hint: val("zahlungsweg") === "inhaber" ? "eigenes Konto oder Karte" : "per Bank → Bankabgleich" },
                { key: "bar", label: "Bar", hint: "aus eigener Tasche" },
              ] as const;
              function pick(k: string) {
                if (k === "bar") {
                  /* Die GbR hat keine Barkasse — bar zahlt ein Inhaber aus eigener Tasche
                     (03.11.2026). Vorgewählt: der Angemeldete, „Privat ausgelegt" → Einlage.
                     Ein Privatkauf bleibt Privatkauf; beides bleibt umstellbar. */
                  const ich = String(val("inhaber_id") ?? "") || (inhaberQ.data?.find((i) => i.ich)?.id ?? "");
                  setForm((p) => ({
                    ...p,
                    zahlungsart: "bar",
                    is_bezahlt: true,
                    ...(val("is_privatentnahme") ? {} : { zahlungsweg: "inhaber" }),
                    inhaber_id: ich,
                  }));
                } else {
                  setForm((p) => ({ ...p, zahlungsart: "konto" }));
                }
              }
              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
                  {opts.map((o) => {
                    const active = sel === o.key;
                    return (
                      <button
                        key={o.key}
                        onClick={() => pick(o.key)}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 2,
                          padding: "10px 12px",
                          borderRadius: 12,
                          cursor: "pointer",
                          textAlign: "left",
                          border: active ? "1.5px solid #3b82f6" : "1px solid var(--m-line)",
                          background: active ? "rgba(59,130,246,0.08)" : "var(--m-card)",
                          color: "var(--m-ink)",
                        }}
                      >
                        <span style={{ fontSize: 13, fontWeight: 700 }}>{o.label}</span>
                        <span style={{ fontSize: 10, color: "var(--m-ink-3)" }}>{o.hint}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            {val("zahlungsart") === "bar" && (
              <div style={{ fontSize: 11, color: "var(--m-ink-3)", margin: "6px 0 0 4px" }}>
                Bar aus eigener Tasche — kein Bankabgleich, gilt als bezahlt. Oben steht, wer bezahlt hat.
              </div>
            )}
          </>

        {/* Projekt-Auswahl (Material / Bewirtung) */}
        {(ausgabeTyp === "lieferant" || ausgabeTyp === "verpflegung") && (
          <Section>
            <Field label="Projekt">
              <ProjektWahl
                projekte={projekteQ.data ?? []}
                value={String(val("projekt_id") ?? "")}
                onChange={(id) => setField("projekt_id", id)}
                loading={projekteQ.isLoading}
              />
            </Field>
          </Section>
        )}

        {/* Helfer-Auswahl */}
        {ausgabeTyp === "helfer" && (
          <Section>
            <Field label="Welcher Helfer?">
              <select
                value={String(val("helfer_id") ?? "")}
                onChange={(e) => setField("helfer_id", e.target.value)}
                style={inputStyle}
              >
                <option value="">— Helfer wählen —</option>
                {teamQ.data
                  ?.filter((m) => m.role === "helfer")
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
              </select>
            </Field>
            {/* Helferrechnungen haben nie Projektbezug → kein Projektfeld.
                (Es geht um die Bilanz/das Guthaben des Helfers — folgt in Welle B-2.) */}
          </Section>
        )}

        {/* Konto-Zuordnung (nur bei fertig erfassten Belegen, nicht im Entwurf) —
            direkt bei Zuordnung/Zahlung, VOR der Notiz. Bankzeile zuordnen +
            erkannte IBAN übernehmen — Vorstufe für DATEV/Überweisung. */}
        {!isDraft && <KontoZuordnungSection beleg={b} />}

        {/* Notiz */}
        <Section>
          <Field label="Notiz">
            <textarea
              value={String(val("notiz") ?? "")}
              onChange={(e) => setField("notiz", e.target.value)}
              style={{ ...inputStyle, minHeight: 70, resize: "vertical" }}
              placeholder="optional"
            />
          </Field>
        </Section>

        {/* Rechnung löschen links, Metadaten rechtsbündig daneben — spart Platz.
            Zahlung (Offen/Bezahlt) ist in den Abschluss-Footer gewandert. */}
        <div
          style={{
            marginTop: 28,
            paddingTop: 16,
            borderTop: "1px solid var(--m-line)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
          {/* Löschen ist immer eine bewusste, bestätigte Aktion — auch bei Entwürfen
              (z. B. bereits eingereichte Helferrechnungen). Der Footer-„Abbrechen"
              verlässt die Seite nur noch, ohne zu löschen. */}
          {(!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 16px",
                borderRadius: 10,
                border: "1px solid #e08272",
                background: "transparent",
                color: "#e08272",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <Trash2 size={15} />
              {versendet ? "Als Fehlerfassung verwerfen" : "Rechnung löschen"}
            </button>
          ) : (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: 10,
                background: "rgba(224,130,114,0.10)",
                border: "1px solid #e08272",
              }}
            >
              <div style={{ fontSize: 13, color: "var(--m-ink)", fontWeight: 600, marginBottom: 4 }}>
                {versendet ? "Beleg verwerfen?" : "Rechnung wirklich löschen?"}
              </div>
              <div style={{ fontSize: 12, color: "var(--m-ink-3)", marginBottom: 12 }}>
                {versendet
                  ? "Der Beleg ist schon beim Steuerberater. Er bleibt deshalb durchgestrichen stehen; Projekt- und Gesellschafterkonto-Buchung fallen weg. Bitte der Kanzlei sagen, dass er nicht zu buchen ist."
                  : "PDF in der Cloud, Buchhaltungseintrag und Daten in der App werden entfernt. Diese Aktion kann nicht rückgängig gemacht werden."}
              </div>
              {versendet && (
                <input
                  value={verwerfenGrund}
                  onChange={(e) => setVerwerfenGrund(e.target.value)}
                  placeholder={dubletten.length ? "Grund, z. B. Dublette" : "Grund (freiwillig)"}
                  style={{ ...inputStyle, marginBottom: 10 }}
                />
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleteM.isPending}
                  style={{
                    padding: "9px 14px",
                    borderRadius: 8,
                    border: "1px solid var(--m-line)",
                    background: "transparent",
                    color: "var(--m-ink)",
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => (versendet
                    ? verwerfenM.mutate(verwerfenGrund.trim() || (dubletten.length ? "Dublette" : ""))
                    : deleteM.mutate())}
                  disabled={deleteM.isPending || verwerfenM.isPending}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "9px 16px",
                    borderRadius: 8,
                    border: "none",
                    background: "#e08272",
                    color: "white",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: deleteM.isPending ? "not-allowed" : "pointer",
                  }}
                >
                  <Trash2 size={13} />
                  {deleteM.isPending || verwerfenM.isPending ? "…" : versendet ? "Ja, Beleg verwerfen" : "Endgültig löschen"}
                </button>
              </div>
              {(deleteM.isError || verwerfenM.isError) && (
                <div style={{ marginTop: 10, fontSize: 12, color: "tomato" }}>
                  {((deleteM.error || verwerfenM.error) as Error).message}
                </div>
              )}
            </div>
          ))}
          </div>
          <div style={{ fontSize: 11, color: "var(--m-ink-3)", textAlign: "right", flexShrink: 0, lineHeight: 1.55 }}>
            <div>Quelle: {b.source}{b.source_ref && ` · ${b.source_ref.slice(0, 12)}`}</div>
            <div>Erstellt: {fmtIso(b.created_at)}</div>
            {b.updated_at && <div>Geändert: {fmtIso(b.updated_at)}</div>}
          </div>
        </div>
      </div>

      {/* Sticky Save-Footer */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "12px 16px calc(env(safe-area-inset-bottom) + 12px)",
          background: "linear-gradient(to top, var(--m-bg) 75%, transparent)",
          display: "flex",
          gap: 10,
        }}
      >
        {/* Abschluss-Aktion = Zahlungsstatus: ein Tipp auf Offen, Überweisen oder
            Bezahlt verarbeitet den Beleg (setzt is_bezahlt/im_zahllauf + finalisiert).
            "Verwerfen" löscht NIE — es verwirft nur lokale Änderungen. Beim Entwurf
            führt der Zurück-Pfeil oben aus dem Beleg; Löschen ist oben, bestätigt. */}
        {!(isDraft && kannUeberweisen) && (
          <button
            onClick={isDraft ? () => nav("/inbox") : () => setForm({})}
            disabled={isDraft ? patchM.isPending || finalizeM.isPending : !hasChanges || patchM.isPending}
            style={{
              ...secondaryBtn,
              ...(isDraft ? {} : { opacity: hasChanges ? 1 : 0.4 }),
            }}
          >
            {isDraft ? "Abbrechen" : "Verwerfen"}
          </button>
        )}
        {(() => {
          // F8: Buttons abhängig vom Status.
          //  - Entwurf: Offen | Bezahlt (finalisiert).
          //  - schon bezahlt: nur „Bestätigen" (kein zweites „Bezahlt").
          //  - einer Buchung zugeordnet (nicht bezahlt): nur „Bezahlt" (kein „Offen").
          //  - sonst: Offen | Bezahlt.
          type Opt = { key: string; label: string; bezahlt?: boolean; primary: boolean; ueberweisen?: boolean };
          let opts: Opt[];
          if (!isDraft && istBezahlt) {
            opts = [{ key: "ok", label: "Bestätigen", bezahlt: undefined, primary: true }];
          } else if (!isDraft && hatBuchung) {
            opts = [{ key: "bezahlt", label: "Bezahlt", bezahlt: true, primary: true }];
          } else if (isDraft && kannUeberweisen) {
            opts = [
              { key: "offen", label: "Offen", bezahlt: false, primary: false },
              { key: "ueberweisen", label: "Überweisen", bezahlt: false, primary: false, ueberweisen: true },
              { key: "bezahlt", label: "Bezahlt", bezahlt: true, primary: true },
            ];
          } else {
            opts = [
              { key: "offen", label: "Offen", bezahlt: false, primary: false },
              { key: "bezahlt", label: "Bezahlt", bezahlt: true, primary: true },
            ];
          }
          const busy = patchM.isPending || finalizeM.isPending;
          // Abschließen erst, wenn Clara fertig gelesen hat — sonst schriebe sie
          // in einen schon gebuchten Beleg.
          const wartet = isDraft && claraLiest;
          return opts.map((opt) => (
            <button
              key={opt.key}
              onClick={() => handleConfirm(opt.bezahlt, opt.ueberweisen)}
              title={opt.ueberweisen ? "Offen in den Ausgangskorb — geht mit der nächsten Bank-Freigabe raus" : undefined}
              disabled={busy || wartet}
              style={{
                flex: 1,
                padding: "13px 12px",
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 700,
                cursor: busy ? "not-allowed" : "pointer",
                border: opt.primary ? "none" : "1.5px solid var(--m-accent, #6db04a)",
                background: opt.primary ? "var(--m-accent, #6db04a)" : "transparent",
                color: opt.primary ? "white" : "var(--m-accent, #6db04a)",
                opacity: busy || wartet ? 0.6 : 1,
              }}
            >
              {busy ? "…" : wartet && opt.primary ? "Clara liest …" : opt.label}
            </button>
          ));
        })()}
      </div>

      {(patchM.isError || finalizeM.isError || deleteM.isError) && (
        <div
          style={{
            position: "fixed",
            bottom: 80,
            left: 16,
            right: 16,
            padding: "10px 14px",
            borderRadius: 10,
            background: "rgba(220,90,50,0.92)",
            color: "white",
            fontSize: 13,
            zIndex: 50,
          }}
        >
          {((patchM.error || finalizeM.error || deleteM.error) as Error)?.message}
        </div>
      )}

      {/* Vollbild-Vorschau — hochauflösendes Bild der ersten Seite, scroll-/zoombar.
          (Bewusst KEIN PDF-iframe: das bleibt auf iOS-Safari häufig leer.) */}
      {fullscreen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            background: "rgba(0,0,0,0.96)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              padding: "calc(env(safe-area-inset-top) + 10px) 12px 10px",
              flexShrink: 0,
            }}
          >
            {/* Zoom-Steuerung: −/Prozent/+ · Doppeltipp aufs Bild zoomt ebenfalls. */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <button
                onClick={() => setZoom((z) => Math.max(1, Math.round((z - 0.5) * 10) / 10))}
                style={zoomBtnStyle}
                aria-label="Verkleinern"
              >
                <Minus size={18} />
              </button>
              <span style={{ color: "white", fontSize: 13, fontWeight: 700, minWidth: 42, textAlign: "center" }}>
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={() => setZoom((z) => Math.min(4, Math.round((z + 0.5) * 10) / 10))}
                style={zoomBtnStyle}
                aria-label="Vergrößern"
              >
                <Plus size={18} />
              </button>
            </div>
            <button
              onClick={() => { setFullscreen(false); setZoom(1); }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "10px 16px",
                borderRadius: 999,
                border: "none",
                background: "rgba(255,255,255,0.18)",
                color: "white",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
              aria-label="Schließen"
            >
              <X size={20} /> Schließen
            </button>
          </div>
          <div
            style={{
              flex: 1,
              overflow: "auto",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-x pan-y pinch-zoom",
              padding: "0 12px calc(env(safe-area-inset-bottom) + 16px)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              justifyContent: "flex-start",
              alignItems: zoom > 1 ? "flex-start" : "center",
            }}
          >
            {(anhaenge.length
              ? [...anhaenge, { page: 0, label: "Original-Scan" }]
              : [{ page: undefined as number | undefined, label: "" }]
            ).map((p) => (
              <div
                key={p.page ?? "single"}
                style={{ width: `${zoom * 100}%`, maxWidth: 900 * zoom, flexShrink: 0 }}
              >
                {p.label && (
                  <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, fontWeight: 600, margin: "0 0 6px 2px" }}>
                    {p.label}
                  </div>
                )}
                <img
                  src={api.thumbnailUrl(b.id, 1400, p.page)}
                  alt={p.label || "Beleg in Vollbild"}
                  onDoubleClick={() => setZoom((z) => (z > 1 ? 1 : 2.5))}
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: 8,
                    background: "white",
                    display: "block",
                    cursor: zoom > 1 ? "zoom-out" : "zoom-in",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── helpers ─────────────────────────────────────

function PageHeader({
  onBack,
  title,
  subtitle,
}: {
  onBack: () => void;
  title: string;
  subtitle?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "calc(env(safe-area-inset-top) + 14px) 16px 10px",
        borderBottom: "1px solid var(--m-line)",
        background: "var(--m-bg)",
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
    >
      <button
        onClick={onBack}
        aria-label="Zurück"
        style={{
          background: "transparent",
          border: "none",
          color: "var(--m-ink-3)",
          padding: 4,
          cursor: "pointer",
          display: "flex",
        }}
      >
        <ArrowLeft size={22} />
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: "var(--m-ink)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 11, color: "var(--m-ink-3)" }}>{subtitle}</div>
        )}
      </div>
    </div>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return <div style={{ marginTop: 18 }}>{children}</div>;
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10, marginTop: 10 }}>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          fontSize: 11,
          color: "var(--m-ink-3)",
          marginBottom: 4,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function TypTile({
  icon,
  label,
  hint,
  color,
  active,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  color: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        textAlign: "left",
        padding: "12px 12px",
        background: active ? color : "var(--m-card)",
        border: `1.5px solid ${active ? color : "var(--m-line)"}`,
        borderRadius: 12,
        color: active ? "white" : "var(--m-ink)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.35 : 1,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <span style={{ color: active ? "white" : color, display: "flex" }}>{icon}</span>
      <div style={{ fontSize: 13, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 10, opacity: 0.85 }}>{hint}</div>
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid var(--m-line)",
  background: "var(--m-card)",
  color: "var(--m-ink)",
  fontSize: 14,
  boxSizing: "border-box",
};

const secondaryBtn: React.CSSProperties = {
  padding: "13px 18px",
  borderRadius: 12,
  border: "1px solid var(--m-line)",
  background: "transparent",
  color: "var(--m-ink)",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

const zoomBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 38,
  height: 38,
  borderRadius: 999,
  border: "none",
  background: "rgba(255,255,255,0.18)",
  color: "white",
  cursor: "pointer",
};


// ─────────────── KI-Herkunft: was hat die KI woher erkannt (transparent) ───────────
// Zeigt die von der KI aus dem Beleg gezogenen Werte + Konfidenz. Die Felder darunter
// bleiben editierbar — unsichere Werte (gelber Punkt) prüft man am (zoombaren) Beleg.

function KiHerkunft({
  konfidenz,
  lieferant,
  datum,
  brutto,
}: {
  konfidenz: { betrag?: number | null; datum?: number | null; lieferant?: number | null };
  lieferant: string;
  datum: string;
  brutto: number;
}) {
  const rows: { label: string; wert: string; konf: number | null | undefined; mono?: boolean }[] = [
    { label: "Lieferant", wert: lieferant || "—", konf: konfidenz?.lieferant },
    { label: "Datum", wert: datum ? fmtDatum(datum) : "—", konf: konfidenz?.datum },
    { label: "Betrag", wert: brutto > 0 ? `${brutto.toFixed(2).replace(".", ",")} €` : "—", konf: konfidenz?.betrag },
  ];

  return (
    <div
      style={{
        marginBottom: 12,
        padding: "10px 12px",
        borderRadius: 12,
        border: "1px solid var(--m-line)",
        background: "var(--m-card)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <Sparkles size={14} style={{ color: "var(--m-ink-3)" }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--m-ink)" }}>Von Clara gelesen</span>
        <span style={{ fontSize: 11, color: "var(--m-ink-3)" }}>· unten anpassbar</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((r) => (
          <div key={r.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ fontSize: 12, color: "var(--m-ink-3)", flexShrink: 0, width: 66 }}>{r.label}</span>
            <span
              style={{
                fontSize: 13,
                color: "var(--m-ink)",
                flex: 1,
                textAlign: "right",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontFamily: r.mono ? "monospace" : undefined,
              }}
            >
              {r.wert}
            </span>
            <KonfDot konf={r.konf} />
          </div>
        ))}
      </div>
    </div>
  );
}

function KonfDot({ konf }: { konf: number | null | undefined }) {
  let color = "var(--m-line)";
  let title = "ohne Konfidenzangabe";
  if (typeof konf === "number") {
    if (konf >= 0.8) {
      color = "#3fae7a";
      title = `sicher erkannt (${Math.round(konf * 100)} %)`;
    } else {
      color = "#e0a53f";
      title = `unsicher (${Math.round(konf * 100)} %) — am Beleg prüfen`;
    }
  }
  return (
    <span
      title={title}
      style={{ width: 9, height: 9, borderRadius: 999, background: color, flexShrink: 0, display: "inline-block" }}
    />
  );
}


// ─────────────── Konto-Zuordnung: passende Bankbuchung + IBAN übernehmen ───────────
//
// Bringt die Desktop-Fähigkeit „Beleg ↔ Bankzeile" nach mobil. Zuordnen setzt den
// Beleg auf 'verbucht'; ab dann holt ihn die Desktop-DATEV-Warteschlange automatisch.
// Bar-Belege brauchen keinen Bankabgleich → Abschnitt entfällt. SEPA/FinTS bleibt Desktop.

function euro(n: number): string {
  return `${(n ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/** Lesbare Meldung: `api.request` legt FastAPIs `detail` schon in `message`. */
function fehlertext(e: unknown): string {
  return e instanceof Error && e.message ? e.message : "Das hat nicht geklappt.";
}

function KontoZuordnungSection({ beleg }: { beleg: Beleg }) {
  const qc = useQueryClient();
  const id = beleg.id;
  const matched = !!beleg.kontobewegung_id;
  const istBar = beleg.zahlungsart === "bar";
  const betrag = beleg.bruttobetrag || beleg.netto || 0;
  const empfaengerZugeordnet =
    (beleg.ausgabe_typ === "helfer" && !!beleg.helfer_id) || !!beleg.lieferant_firma_id;
  // IBAN nur anbieten, wenn sie am Beleg erkannt wurde, ein Empfänger zugeordnet ist
  // UND dort noch KEINE IBAN hinterlegt ist — sonst gar nicht erst anzeigen.
  const ibanAnbieten = !!beleg.erkannte_iban && empfaengerZugeordnet && !beleg.empfaenger_hat_iban;

  // Zahllauf ist ein EXPLIZITER Opt-in: der Nutzer übernimmt den Beleg aktiv in den
  // Überweisungskorb. `zahllaufEligible` sagt nur, ob das überhaupt sinnvoll ist
  // (offen, per Konto, auf Firmenrechnung, keiner Bankzeile zugeordnet, nicht angewiesen).
  const zahllaufEligible =
    !matched &&
    !istBar &&
    !beleg.is_bezahlt &&
    !beleg.is_privatentnahme &&
    (beleg.zahlungsweg || "gbr") === "gbr" &&
    !["angewiesen", "ausgefuehrt"].includes(beleg.ueberweisung_status || "none");
  const imZahllauf = !!beleg.im_zahllauf && zahllaufEligible;
  // Solange der Beleg in Erfassung ist, darf er markiert werden — im Korb
  // auftauchen tut er erst nach dem Abschliessen (der Betrag ist bis dahin
  // KI-Schaetzung). Das gehoert an den Knopf, sonst sucht man ihn im Zahllauf.
  const nochInErfassung = istEntwurfStatus(beleg.status);
  const zahlbereit = empfaengerZugeordnet && beleg.empfaenger_hat_iban && betrag > 0;
  const zahllaufFarbe = zahlbereit ? "#3fae7a" : ibanAnbieten ? "#e0a53f" : "#8a8a8a";
  // Zahlbereite Belege legt der Server sofort als Auftrag für die Bank-App an
  // (16.10.2026). Steht einer trotzdem noch im Korb, hält ihn eine Warnung an.
  const zahllaufText = zahlbereit
    ? "angehalten — bitte im Kontor unter Zahllauf prüfen"
    : ibanAnbieten
      ? "IBAN noch übernehmen (unten)"
      : !empfaengerZugeordnet
        ? "Empfänger fehlt noch"
        : betrag <= 0
          ? "Betrag fehlt noch"
          : "keine IBAN hinterlegt";

  const buchungenQ = useQuery({
    queryKey: ["beleg-matching", id],
    queryFn: () => api.matchingBuchungen(id),
    enabled: !matched && !istBar && betrag > 0,
    staleTime: 30 * 1000,
  });

  const matchM = useMutation({
    mutationFn: (kbId: string) => api.matchBuchung(id, kbId),
    onSuccess: (b) => {
      qc.setQueryData(["beleg", id], b);
      qc.invalidateQueries({ queryKey: ["belege-recent"] });
      // Zahllauf + DATEV-Karte hängen am Status → mit auffrischen.
      qc.invalidateQueries({ queryKey: ["zahllauf"] });
      qc.invalidateQueries({ queryKey: ["datev-pending"] });
      qc.invalidateQueries({ queryKey: ["beleg-matching", id] });
    },
  });
  const unmatchM = useMutation({
    mutationFn: () => api.unmatchBuchung(id),
    onSuccess: (b) => {
      qc.setQueryData(["beleg", id], b);
      qc.invalidateQueries({ queryKey: ["belege-recent"] });
      // Zahllauf + DATEV-Karte hängen am Status → mit auffrischen.
      qc.invalidateQueries({ queryKey: ["zahllauf"] });
      qc.invalidateQueries({ queryKey: ["datev-pending"] });
      qc.invalidateQueries({ queryKey: ["beleg-matching", id] });
    },
  });
  const zahllaufM = useMutation({
    mutationFn: (im: boolean) => api.patchBeleg(id, { im_zahllauf: im }),
    onSuccess: (b) => {
      qc.setQueryData(["beleg", id], b);
      qc.invalidateQueries({ queryKey: ["belege-recent"] });
      qc.invalidateQueries({ queryKey: ["zahllauf"] });
    },
  });
  const ibanM = useMutation({
    mutationFn: () => api.uebernehmeIban(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["beleg", id] }),
  });

  // Bar bezahlt oder privat ausgelegt → kein Bankabgleich: das Geld lief nie über das
  // GbR-Konto (geht ohne Bankzeile an DATEV, mit Eigenbeleg).
  if ((istBar || beleg.zahlungsweg === "inhaber") && !matched) return null;

  return (
    <Section>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Banknote size={16} style={{ color: "var(--m-ink-3)" }} />
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--m-ink)" }}>Bankbuchung</div>
        </div>
        {/* Brutto + Datum der Rechnung — zum Abgleich mit der Bankzeile. */}
        <div style={{ fontSize: 12, color: "var(--m-ink-3)", whiteSpace: "nowrap" }}>
          {betrag > 0 ? euro(betrag) : "—"}
          {beleg.datum && ` · ${fmtDatum(beleg.datum)}`}
        </div>
      </div>

      {/* Zahllauf = expliziter Opt-in: der Nutzer übernimmt den offenen Beleg aktiv
          in den Überweisungskorb. Solange er offen und keiner Bankzeile zugeordnet
          ist, kann er übernommen/entfernt werden. */}
      {zahllaufEligible && (
        imZahllauf ? (
          <div
            style={{
              marginBottom: 8,
              padding: "8px 11px",
              borderRadius: 9,
              border: `1px solid ${zahllaufFarbe}`,
              background: `${zahllaufFarbe}1a`,
              display: "flex",
              alignItems: "center",
              gap: 7,
              fontSize: 12,
              color: "var(--m-ink)",
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: 99, background: zahllaufFarbe, flexShrink: 0 }} />
            <span style={{ flex: 1 }}>
              <b>Im Ausgangskorb</b> ·{" "}
              {nochInErfassung ? "geht raus, sobald der Beleg abgeschlossen ist" : zahllaufText}
            </span>
            <button
              onClick={() => zahllaufM.mutate(false)}
              disabled={zahllaufM.isPending}
              style={{
                background: "none", border: "none", color: "var(--m-ink-3)",
                fontSize: 11.5, textDecoration: "underline", cursor: "pointer", padding: 0, flexShrink: 0,
              }}
            >
              entfernen
            </button>
          </div>
        ) : (
          <button
            onClick={() => zahllaufM.mutate(true)}
            disabled={zahllaufM.isPending}
            style={{
              marginBottom: 8,
              width: "100%",
              padding: "9px 11px",
              borderRadius: 9,
              border: "1px dashed var(--m-line)",
              background: "transparent",
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12.5,
              color: "var(--m-ink-2)",
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: 15 }}>＋</span>
            <span>
              In den Ausgangskorb legen{" "}
              <span style={{ color: "var(--m-ink-3)" }}>
                {nochInErfassung ? "— geht raus, sobald der Beleg abgeschlossen ist" : "— geht mit der nächsten Bank-Freigabe raus"}
              </span>
            </span>
          </button>
        )
      )}
      {!matched && ["angewiesen", "ausgefuehrt"].includes(beleg.ueberweisung_status || "") && (
        <div
          style={{
            marginBottom: 8,
            padding: "8px 11px",
            borderRadius: 9,
            border: "1px solid #3fae7a",
            background: "#3fae7a1a",
            display: "flex",
            alignItems: "center",
            gap: 7,
            fontSize: 12,
            color: "var(--m-ink)",
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: 99, background: "#3fae7a", flexShrink: 0 }} />
          <span style={{ flex: 1 }}>
            {beleg.ueberweisung_status === "angewiesen" ? (
              <>
                <b>Im Ausgangskorb</b> · geht mit der nächsten Bank-Freigabe raus
              </>
            ) : (
              <>
                <b>Überwiesen</b> · die Bankzeile ordnet sich von selbst zu
              </>
            )}
          </span>
        </div>
      )}
      {/* Ohne sichtbare Fehlerstelle sah ein gescheiterter Klick genauso aus
          wie gar kein Klick — der Knopf wirkte tot. */}
      {zahllaufM.isError && (
        <div style={{ marginBottom: 8, fontSize: 12, color: "#d05a5a" }}>
          {fehlertext(zahllaufM.error)}
        </div>
      )}

      {matched ? (
        <div
          style={{
            padding: "12px 14px",
            borderRadius: 10,
            background: "var(--m-card)",
            border: "1px solid var(--m-line)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--m-ink)" }}>
            <CheckCircle2 size={16} style={{ color: "#3fae7a" }} />
            Einer Bankbuchung zugeordnet
          </div>
          <button
            onClick={() => unmatchM.mutate()}
            disabled={unmatchM.isPending}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 12px",
              borderRadius: 8,
              border: "1px solid var(--m-line)",
              background: "transparent",
              color: "var(--m-ink-3)",
              fontSize: 12,
              cursor: unmatchM.isPending ? "not-allowed" : "pointer",
            }}
          >
            <X size={13} /> {unmatchM.isPending ? "Löse…" : "Lösen"}
          </button>
        </div>
      ) : betrag <= 0 ? (
        <div style={{ fontSize: 12, color: "var(--m-ink-3)", padding: "4px 2px" }}>
          Kein Betrag erfasst — erst den Betrag ergänzen, dann kann eine Bankbuchung zugeordnet werden.
        </div>
      ) : (
        <>
          {buchungenQ.isLoading && (
            <div style={{ fontSize: 12, color: "var(--m-ink-3)", padding: "4px 2px" }}>
              Suche passende Buchungen…
            </div>
          )}
          {buchungenQ.data && buchungenQ.data.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--m-ink-3)", padding: "4px 2px" }}>
              Keine Bankbuchung über {euro(betrag)} gefunden. Der nächste Konto-Sync (Desktop)
              bringt womöglich neue Buchungen.
            </div>
          )}
          {buchungenQ.isError && (
            <div style={{ fontSize: 12, color: "tomato", padding: "4px 2px" }}>
              {(buchungenQ.error as Error).message}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
            {buchungenQ.data?.map((kb) => (
              <div
                key={kb.id}
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: "var(--m-card)",
                  border: "1px solid var(--m-line)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, color: "var(--m-ink)", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {kb.gegenpartei_name || kb.verwendungszweck || "Buchung"}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--m-ink-3)", marginTop: 2 }}>
                    {fmtDatum(kb.buchungsdatum)} · {euro(Math.abs(kb.betrag))}
                    {kb.hat_beleg && " · schon belegt"}
                  </div>
                </div>
                <button
                  onClick={() => matchM.mutate(kb.id)}
                  disabled={matchM.isPending || kb.hat_beleg}
                  style={{
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "none",
                    background: kb.hat_beleg ? "var(--m-line)" : "#3fae7a",
                    color: kb.hat_beleg ? "var(--m-ink-3)" : "white",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: matchM.isPending || kb.hat_beleg ? "not-allowed" : "pointer",
                  }}
                >
                  <Check size={13} /> Zuordnen
                </button>
              </div>
            ))}
          </div>
          {matchM.isError && (
            <div style={{ fontSize: 12, color: "tomato", marginTop: 6 }}>
              {(matchM.error as Error).message}
            </div>
          )}
        </>
      )}

      {/* IBAN nur anbieten, wenn beim Helfer/Lieferanten noch KEINE hinterlegt ist —
          dann ist die Übernahme echt nützlich statt nur Anzeige. */}
      {ibanAnbieten && (
        <div
          style={{
            marginTop: 10,
            padding: "10px 12px",
            borderRadius: 10,
            background: "var(--m-card)",
            border: "1px dashed var(--m-line)",
          }}
        >
          <div style={{ fontSize: 11, color: "var(--m-ink-3)", marginBottom: 6 }}>
            {beleg.ausgabe_typ === "helfer" ? "Helfer" : "Lieferant"} hat noch keine IBAN —
            am Beleg erkannt:
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ fontSize: 13, color: "var(--m-ink)", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis" }}>
              {beleg.erkannte_iban}
            </div>
            <button
              onClick={() => ibanM.mutate()}
              disabled={ibanM.isPending || ibanM.isSuccess}
              style={{
                flexShrink: 0,
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid var(--m-line)",
                background: "transparent",
                color: ibanM.isSuccess ? "#3fae7a" : "var(--m-ink)",
                fontSize: 12,
                fontWeight: 700,
                cursor: ibanM.isPending || ibanM.isSuccess ? "default" : "pointer",
              }}
            >
              {ibanM.isSuccess ? "Übernommen ✓" : ibanM.isPending ? "…" : "Übernehmen"}
            </button>
          </div>
          {ibanM.isError && (
            <div style={{ fontSize: 12, color: "tomato", marginTop: 6 }}>
              {(ibanM.error as Error).message}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}


// ─────────────── Lieferanten-Zeile: Erkennung + Picker + neu anlegen ───────────────

/** Lockere Normalisierung für client-seitige Vorschläge (Rechtsformen/Sonderzeichen weg). */
function normLief(s: string): string {
  return s
    .toLowerCase()
    .replace(/\b(gmbh|mbh|ag|kg|ohg|ug|co|deutschland|filiale|baumarkt)\b/g, " ")
    .replace(/[^a-z0-9äöüß]+/g, " ")
    .trim();
}

/** Bester Listen-Treffer für einen roh erkannten Namen (nur Vorschlag, nicht verbindlich). */
function suggestFirma(roh: string, list: Lieferant[]): Lieferant | null {
  const n = normLief(roh);
  if (!n) return null;
  for (const l of list) {
    const ln = normLief(l.name);
    if (ln && (ln === n || ln.includes(n) || n.includes(ln))) return l;
  }
  const toks = new Set(n.split(" ").filter((t) => t.length >= 3));
  for (const l of list) {
    const lt = normLief(l.name).split(" ").filter(Boolean);
    if (lt.some((t) => t.length >= 3 && toks.has(t))) return l;
  }
  return null;
}

/** Geldbetrag-Feld: leerbar (kein klebendes 0), zeigt Preise mit 2 Dezimalstellen
 *  (3,80 statt 3,8). Committed nur valide Werte; leer/ungültig → 0. */
function MoneyInput({
  value,
  onCommit,
  placeholder,
}: {
  value: number;
  onCommit: (n: number) => void;
  placeholder?: string;
}) {
  const fmt = (n: number) => (n > 0 ? n.toFixed(2).replace(".", ",") : "");
  const [text, setText] = useState(fmt(value));
  const [focused, setFocused] = useState(false);
  // Externe Änderung (z.B. KI-Extraktion) übernehmen, solange nicht aktiv editiert.
  useEffect(() => {
    if (!focused) setText(fmt(value));
  }, [value, focused]);
  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onChange={(e) => setText(e.target.value.replace(/[^0-9.,]/g, ""))}
      onBlur={() => {
        setFocused(false);
        const n = parseFloat(text.replace(",", "."));
        if (isFinite(n) && n > 0) {
          const r = Math.round(n * 100) / 100;
          onCommit(r);
          setText(fmt(r));
        } else {
          onCommit(0);
          setText("");
        }
      }}
      style={{ ...inputStyle, minWidth: 0 }}
    />
  );
}

function LieferantField({
  roh,
  firmaId,
  lieferanten,
  isHelfer,
  onRohChange,
  onPickFirma,
}: {
  roh: string;
  firmaId: string;
  lieferanten: Lieferant[];
  isHelfer: boolean;
  onRohChange: (v: string) => void;
  onPickFirma: (firmaId: string) => void;
}) {
  const qc = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const createM = useMutation({
    mutationFn: (name: string) => api.createLieferant(name, true),
    onSuccess: (lf) => {
      qc.invalidateQueries({ queryKey: ["scanflow-lieferanten"] });
      onPickFirma(lf.id);
      setPickerOpen(false);
    },
  });

  // Helfer sind keine Lieferanten — einfaches Textfeld, kein Listen-Matching.
  if (isHelfer) {
    return (
      <Field label="Aussteller (Rechnung)">
        <input
          value={roh}
          onChange={(e) => onRohChange(e.target.value)}
          style={inputStyle}
          placeholder="Name auf der Helfer-Rechnung"
        />
      </Field>
    );
  }

  const matched = firmaId ? lieferanten.find((l) => l.id === firmaId) ?? null : null;
  const sammel = lieferanten.find((l) => l.name.trim().toLowerCase() === "sammellieferant") ?? null;
  const suggestion = !firmaId ? suggestFirma(roh, lieferanten) : null;
  const rohInList = !!roh.trim() && lieferanten.some((l) => normLief(l.name) === normLief(roh));

  const q = query.trim().toLowerCase();
  const filtered = (q
    ? lieferanten.filter((l) => l.name.toLowerCase().includes(q))
    : lieferanten
  ).slice(0, 8);

  return (
    <Field label="Lieferant">
      {matched ? (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={badgeOk}>
              <CheckCircle2 size={13} /> erkannt
            </span>
            <strong style={{ fontSize: 14, color: "var(--m-ink)" }}>{matched.name}</strong>
            <button
              onClick={() => {
                onPickFirma("");
                setPickerOpen(true);
              }}
              style={linkBtn}
            >
              ändern
            </button>
          </div>
          {roh.trim() && normLief(roh) !== normLief(matched.name) && (
            <div style={subtleTxt}>Beleg: {roh}</div>
          )}
        </div>
      ) : (
        <>
          <input
            value={roh}
            onChange={(e) => onRohChange(e.target.value)}
            style={inputStyle}
            placeholder="z.B. HORNBACH Baumarkt"
          />
          {suggestion && (
            <button onClick={() => onPickFirma(suggestion.id)} style={suggestBtn}>
              <Check size={14} /> Vermutlich «{suggestion.name}» — übernehmen
            </button>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            {roh.trim() && !rohInList && (
              <button
                onClick={() => createM.mutate(roh.trim())}
                disabled={createM.isPending}
                style={newBtn}
              >
                <Plus size={14} /> «{roh.trim()}» neu anlegen
              </button>
            )}
            {sammel && (
              <button onClick={() => onPickFirma(sammel.id)} style={newBtn}>
                <Building2 size={14} /> Sammellieferant
              </button>
            )}
            <button onClick={() => setPickerOpen((o) => !o)} style={pickBtn}>
              <Search size={14} /> aus Liste wählen
            </button>
          </div>
          {roh.trim() && !rohInList && <div style={subtleTxt}>Noch nicht in der Liste</div>}
        </>
      )}

      {pickerOpen && (
        <div style={pickerBox}>
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <Search size={15} style={{ position: "absolute", left: 10, color: "var(--m-ink-3)" }} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Lieferant suchen…"
              style={{ ...inputStyle, paddingLeft: 32 }}
            />
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto", marginTop: 6 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "10px 4px", color: "var(--m-ink-3)", fontSize: 13 }}>
                Kein Lieferant gefunden.
              </div>
            ) : (
              filtered.map((l) => (
                <button
                  key={l.id}
                  onClick={() => {
                    onPickFirma(l.id);
                    setPickerOpen(false);
                    setQuery("");
                  }}
                  style={pickRow}
                >
                  <Building2 size={14} color="var(--m-ink-3)" /> {l.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}
      {createM.isError && (
        <div style={{ ...subtleTxt, color: "tomato" }}>{(createM.error as Error).message}</div>
      )}
    </Field>
  );
}

const badgeOk: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 10,
  fontWeight: 700,
  color: "white",
  background: "var(--m-accent, #6db04a)",
  padding: "3px 8px",
  borderRadius: 999,
};

const linkBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--m-ink-3)",
  fontSize: 12,
  textDecoration: "underline",
  cursor: "pointer",
  padding: 0,
};

const subtleTxt: React.CSSProperties = {
  fontSize: 11,
  color: "var(--m-ink-3)",
  marginTop: 6,
};

const suggestBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  marginTop: 8,
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid var(--m-accent, #6db04a)",
  background: "rgba(109,176,74,0.08)",
  color: "var(--m-accent, #6db04a)",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const newBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid var(--m-line)",
  background: "var(--m-card)",
  color: "var(--m-ink)",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const pickBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid var(--m-line)",
  background: "transparent",
  color: "var(--m-ink-2)",
  fontSize: 13,
  cursor: "pointer",
};

const pickerBox: React.CSSProperties = {
  marginTop: 10,
  padding: 10,
  borderRadius: 10,
  border: "1px solid var(--m-line)",
  background: "var(--m-card)",
};

const pickRow: React.CSSProperties = {
  width: "100%",
  textAlign: "left",
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "9px 8px",
  background: "transparent",
  border: "none",
  borderRadius: 8,
  color: "var(--m-ink)",
  fontSize: 14,
  cursor: "pointer",
};

function fmtDatum(s: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
}

function fmtIso(s: string | null): string {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return s;
  }
}
